import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { hasRole, ROLE_LABELS } from '../../config/permissions';
import {
  HomeIcon,
  UserGroupIcon,
  ArrowRightOnRectangleIcon,
  BoltIcon,
  CloudArrowDownIcon,
  UsersIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  CircleStackIcon,
  GlobeAltIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline';
import DeviceIcon from '../DeviceIcon';
import brandLogo from '../../assets/brand-logo.png';
import greenLinesIcon from '../../assets/green-lines-icon.png';
import DropdownMenu from '../DropdownMenu';

// Three lines with a chevron pointing back at them: the drawer is open and
// this puts it away. Collapsed, the brand's own green lines take its place.
const CollapseIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M3 6h13" />
    <path d="M3 12h10" />
    <path d="M3 18h13" />
    <path d="M20 9l-3 3 3 3" />
  </svg>
);

// The banner names the page, the way the phone app's desktop layout does.
// Detail routes are tested before their list route so the looser match cannot
// swallow them.
const getPageTitle = (pathname) => {
  if (pathname.includes('/logs/activity')) return 'Activity Profile';
  if (pathname.includes('/logs/nginx')) return 'Nginx Traffic';
  if (pathname.includes('/logs/system')) return 'System Logs';
  if (pathname.includes('/devices/')) return 'Device Details';
  if (pathname.includes('/devices')) return 'Device Management';
  if (pathname.includes('/users/')) return 'User Details';
  if (pathname.includes('/users')) return 'User Management';
  if (pathname.includes('/employees')) return 'Employee Management';
  if (pathname.includes('/vendors')) return 'Vendor Management';
  if (pathname.includes('/events')) return 'Live Events';
  if (pathname.includes('/history')) return 'History Sync Management';
  if (pathname.includes('/migration')) return 'DM510 Migration';
  if (pathname.includes('/overview')) return 'Overview';
  return 'Dashboard';
};

const ModernLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [logsExpanded, setLogsExpanded] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userRole = user?.role;
  const isDeveloper = hasRole(userRole, 'developer');
  const isAdminOrHigher = hasRole(userRole, 'admin');

  // Regular menu items (non-expandable)
  const menuItems = [
    // Overview, Live Events and History Sync are developer tools
    ...(isDeveloper ? [{ id: 'overview', label: 'Overview', icon: HomeIcon, path: '/dashboard/overview' }] : []),
    { id: 'devices', label: 'Devices', icon: DeviceIcon, path: '/dashboard/devices' },
    { id: 'users', label: 'Users', icon: UserGroupIcon, path: '/dashboard/users' },
    // Employees - Admin and Developer only
    ...(isAdminOrHigher ? [{ id: 'employees', label: 'Employees', icon: UsersIcon, path: '/dashboard/employees' }] : []),
    { id: 'vendors', label: 'Vendors', icon: BuildingOfficeIcon, path: '/dashboard/vendors' },
    ...(isDeveloper ? [
      { id: 'events', label: 'Live Events', icon: BoltIcon, path: '/dashboard/events' },
      { id: 'history', label: 'History Sync', icon: CloudArrowDownIcon, path: '/dashboard/history' },
      // The DM510 boxes on the old server and their move to this cloud
      { id: 'migration', label: 'Migration', icon: ArrowsRightLeftIcon, path: '/dashboard/migration' },
    ] : []),
  ];

  // Logs sub-menu items (Developer only)
  const logsMenuItems = [
    { id: 'logs-system', label: 'System Logs', icon: CircleStackIcon, path: '/dashboard/logs/system' },
    { id: 'logs-nginx', label: 'Nginx Traffic', icon: GlobeAltIcon, path: '/dashboard/logs/nginx' },
  ];

  // Check if any logs route is active
  const isLogsActive = location.pathname.startsWith('/dashboard/logs');

  // Auto-expand logs menu when on a logs route
  React.useEffect(() => {
    if (isLogsActive) {
      setLogsExpanded(true);
    }
  }, [isLogsActive]);

  const isActive = (path) => location.pathname.startsWith(path);

  // Collapsed there is no room for a sub-menu, so opening Logs opens the
  // drawer first and brings the sub-menu with it.
  const handleLogsClick = () => {
    if (!sidebarOpen) {
      setSidebarOpen(true);
      setLogsExpanded(true);
      return;
    }
    setLogsExpanded(!logsExpanded);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Drawer */}
      <aside
        className={`relative shrink-0 bg-white transition-all duration-300 ease-out ${
          sidebarOpen ? 'w-[280px]' : 'w-20'
        }`}
      >
        {/* The drawer's edge starts below the navy band, so the band and the
            banner beside it read as one unbroken strip. */}
        <div className="pointer-events-none absolute bottom-0 right-0 top-[4.5rem] w-px bg-primary-900/10" />

        <div className="relative flex h-full flex-col">
          {/* Brand band - same height and colour as the banner to its right */}
          <div className="relative flex h-[4.5rem] shrink-0 items-center bg-primary-900 px-4">
            {sidebarOpen && (
              <img src={brandLogo} alt="Dryer Master" className="block h-[34px] w-auto" />
            )}

            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label={sidebarOpen ? 'Collapse menu' : 'Expand menu'}
              title={sidebarOpen ? 'Collapse menu' : 'Expand menu'}
              className={`absolute top-1/2 -translate-y-1/2 rounded-full p-1.5 transition-all ${
                sidebarOpen
                  ? 'right-4 border border-white/25 bg-white/10 hover:border-white/45 hover:bg-white/20'
                  : 'left-1/2 -translate-x-1/2 bg-transparent'
              }`}
            >
              {sidebarOpen ? (
                <CollapseIcon className="h-6 w-6 text-white" />
              ) : (
                <img src={greenLinesIcon} alt="" className="block h-7 w-7" />
              )}
            </button>
          </div>

          {/* A thread of brand blue under the band, drifting slowly */}
          {sidebarOpen && (
            <div
              className="mx-4 h-px shrink-0 animate-bg-sweep opacity-10"
              style={{
                backgroundImage: 'linear-gradient(90deg, transparent, #466ec3, transparent)',
                backgroundSize: '200% 100%',
              }}
            />
          )}

          {/* Menu Items */}
          <nav className="flex-1 space-y-1 overflow-y-auto p-4">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <button
                  key={item.id}
                  onClick={() => navigate(item.path)}
                  title={sidebarOpen ? '' : item.label}
                  className={`
                    w-full flex items-center gap-3 py-3.5 rounded-xl overflow-hidden
                    border transition-all duration-200 relative group
                    ${sidebarOpen ? 'px-4' : 'justify-center px-0'}
                    ${active
                      ? 'border-primary-600/20 bg-gradient-to-br from-primary-600/10 to-primary-900/5'
                      : 'border-transparent text-gray-700 hover:border-primary-600/10 hover:bg-primary-600/5 hover:translate-x-0.5'
                    }
                  `}
                >
                  {active && (
                    <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-br from-primary-600 to-primary-900" />
                  )}

                  <Icon className={`w-5 h-5 shrink-0 transition-transform group-hover:scale-110 ${active ? 'text-primary-600' : 'text-gray-500'}`} />
                  {sidebarOpen && <span className={active ? 'font-semibold text-primary-600' : 'font-medium'}>{item.label}</span>}
                </button>
              );
            })}

            {/* Logs Expandable Section - Developer only */}
            {isDeveloper && (
              <div className="pt-1">
                {/* Logs Parent Button */}
                <button
                  onClick={handleLogsClick}
                  title={sidebarOpen ? '' : 'Logs'}
                  className={`
                    w-full flex items-center gap-3 py-3.5 rounded-xl overflow-hidden
                    border transition-all duration-200 relative group
                    ${sidebarOpen ? 'justify-between px-4' : 'justify-center px-0'}
                    ${isLogsActive
                      ? 'border-primary-600/20 bg-gradient-to-br from-primary-600/10 to-primary-900/5'
                      : 'border-transparent text-gray-700 hover:border-primary-600/10 hover:bg-primary-600/5 hover:translate-x-0.5'
                    }
                  `}
                >
                  <div className="flex items-center gap-3">
                    {isLogsActive && (
                      <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-br from-primary-600 to-primary-900" />
                    )}
                    <DocumentTextIcon className={`w-5 h-5 shrink-0 transition-transform group-hover:scale-110 ${isLogsActive ? 'text-primary-600' : 'text-gray-500'}`} />
                    {sidebarOpen && <span className={isLogsActive ? 'font-semibold text-primary-600' : 'font-medium'}>Logs</span>}
                  </div>
                  {sidebarOpen && (logsExpanded ? (
                    <ChevronDownIcon className={`w-4 h-4 ${isLogsActive ? 'text-primary-600' : 'text-gray-400'}`} />
                  ) : (
                    <ChevronRightIcon className={`w-4 h-4 ${isLogsActive ? 'text-primary-600' : 'text-gray-400'}`} />
                  ))}
                </button>

                {/* Logs Sub-menu */}
                <AnimatePresence>
                  {logsExpanded && sidebarOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="pl-4 pt-1 space-y-1">
                        {logsMenuItems.map((item) => {
                          const Icon = item.icon;
                          const active = location.pathname === item.path;

                          return (
                            <button
                              key={item.id}
                              onClick={() => navigate(item.path)}
                              className={`
                                w-full flex items-center gap-3 px-4 py-2.5 rounded-lg
                                transition-all duration-200 text-sm
                                ${active
                                  ? 'bg-primary-600/[0.08] text-primary-600 font-semibold'
                                  : 'text-gray-600 hover:bg-primary-600/5 hover:translate-x-0.5'
                                }
                              `}
                            >
                              <Icon className={`w-4 h-4 ${active ? 'text-primary-600' : 'text-gray-400'}`} />
                              <span>{item.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </nav>

          {/* Bottom Section */}
          <div className="shrink-0 p-4">
            <button
              onClick={handleLogout}
              title={sidebarOpen ? '' : 'Logout'}
              className={`w-full flex items-center gap-3 py-2.5 rounded-xl text-gray-700 hover:bg-red-50 hover:text-red-600 transition-all duration-300 group ${
                sidebarOpen ? 'px-4' : 'justify-center px-0'
              }`}
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5 shrink-0 text-gray-500 group-hover:text-red-500 transition-colors" />
              {sidebarOpen && <span className="font-medium">Logout</span>}
            </button>

            {sidebarOpen && (
              <p className="mt-4 text-center text-xs text-gray-400">v1.0.0</p>
            )}
          </div>
        </div>
      </aside>

      {/* Banner and page content */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-[4.5rem] shrink-0 items-center justify-between bg-primary-900 px-8 shadow-lg">
          <h1 className="truncate text-2xl font-semibold text-white">
            {getPageTitle(location.pathname)}
          </h1>

          {/* Account menu */}
          <DropdownMenu
            trigger={({ toggle }) => (
              <button
                onClick={toggle}
                className="flex items-center gap-3 rounded-lg p-1 transition-colors hover:bg-white/10"
                title="Account"
              >
                <div className="text-right">
                  <p className="text-sm font-medium text-white">{user?.email?.split('@')[0]}</p>
                  <p className="text-xs text-primary-200">{ROLE_LABELS[userRole] || userRole}</p>
                </div>
                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white/30">
                  <span className="text-white font-medium">
                    {user?.email?.charAt(0).toUpperCase()}
                  </span>
                </div>
              </button>
            )}
          >
            <div className="border-b border-gray-100 px-4 py-2">
              <p className="truncate text-sm font-medium text-gray-900">{user?.email}</p>
              <p className="text-xs text-gray-500">{ROLE_LABELS[userRole] || userRole}</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-red-50 hover:text-red-600"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              Logout
            </button>
          </DropdownMenu>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ModernLayout;
